import React from 'react';
import { Globe, AlertTriangle, Clock } from 'lucide-react';
import { GeneralConfig } from '../PlatformSettings';

interface GeneralSettingsFormProps {
  settings: GeneralConfig;
  onUpdate: (updates: Partial<GeneralConfig>) => void;
}

export const GeneralSettingsForm: React.FC<GeneralSettingsFormProps> = ({ settings, onUpdate }) => {
  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      let updates: any = {};
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        updates = {
          [parent]: {
            ...settings[parent as keyof GeneralConfig],
            [child]: value,
          },
        };
      }
      
      onUpdate(updates);
    } else {
      onUpdate({ [field]: value });
    }
  };

  const timezones = [
    'America/Toronto',
    'America/Vancouver',
    'America/Edmonton',
    'America/Winnipeg',
    'America/Halifax',
    'America/St_Johns',
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
  ];

  const dateFormats = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY-MM-DD',
    'DD-MM-YYYY',
    'MM-DD-YYYY',
  ];

  const currencies = [
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Globe className="w-6 h-6 text-blue-500 mr-3" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">General Platform Settings</h3>
          <p className="text-sm text-gray-600">Configure basic platform information and preferences</p>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Site Name *
          </label>
          <input
            type="text"
            value={settings.siteName}
            onChange={(e) => handleInputChange('siteName', e.target.value)}
            placeholder="ERPCA Platform"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Site URL *
          </label>
          <input
            type="url"
            value={settings.siteUrl}
            onChange={(e) => handleInputChange('siteUrl', e.target.value)}
            placeholder="https://erpca.ca"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Support Email *
        </label>
        <input
          type="email"
          value={settings.supportEmail}
          onChange={(e) => handleInputChange('supportEmail', e.target.value)}
          placeholder="support@erpca.ca"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Localization */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={settings.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Format
          </label>
          <select
            value={settings.dateFormat}
            onChange={(e) => handleInputChange('dateFormat', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {dateFormats.map(format => (
              <option key={format} value={format}>{format}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Currency
          </label>
          <select
            value={settings.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {currencies.map(currency => (
              <option key={currency.code} value={currency.code}>
                {currency.code} - {currency.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            value={settings.language}
            onChange={(e) => handleInputChange('language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-4">Platform Features</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="tenant-registration"
              checked={settings.features.tenantRegistration}
              onChange={(e) => handleInputChange('features.tenantRegistration', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="tenant-registration" className="ml-2 block text-sm text-gray-900">
              Tenant Registration
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="multi-tenancy"
              checked={settings.features.multiTenancy}
              onChange={(e) => handleInputChange('features.multiTenancy', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="multi-tenancy" className="ml-2 block text-sm text-gray-900">
              Multi-Tenancy
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="api-access"
              checked={settings.features.apiAccess}
              onChange={(e) => handleInputChange('features.apiAccess', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="api-access" className="ml-2 block text-sm text-gray-900">
              API Access
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="webhooks"
              checked={settings.features.webhooks}
              onChange={(e) => handleInputChange('features.webhooks', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="webhooks" className="ml-2 block text-sm text-gray-900">
              Webhooks
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="analytics"
              checked={settings.features.analytics}
              onChange={(e) => handleInputChange('features.analytics', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="analytics" className="ml-2 block text-sm text-gray-900">
              Analytics
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="audit-logs"
              checked={settings.features.auditLogs}
              onChange={(e) => handleInputChange('features.auditLogs', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="audit-logs" className="ml-2 block text-sm text-gray-900">
              Audit Logs
            </label>
          </div>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center">
          <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
          Maintenance Mode
        </h4>
        
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="maintenance-enabled"
              checked={settings.maintenance.enabled}
              onChange={(e) => handleInputChange('maintenance.enabled', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="maintenance-enabled" className="ml-2 block text-sm text-gray-900">
              Enable Maintenance Mode
            </label>
          </div>

          {settings.maintenance.enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maintenance Message
                </label>
                <textarea
                  value={settings.maintenance.message}
                  onChange={(e) => handleInputChange('maintenance.message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="System is under maintenance. Please try again later."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled Start (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={settings.maintenance.scheduledStart || ''}
                    onChange={(e) => handleInputChange('maintenance.scheduledStart', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled End (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={settings.maintenance.scheduledEnd || ''}
                    onChange={(e) => handleInputChange('maintenance.scheduledEnd', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed IP Addresses (One per line)
                </label>
                <textarea
                  value={settings.maintenance.allowedIPs.join('\n')}
                  onChange={(e) => handleInputChange('maintenance.allowedIPs', e.target.value.split('\n').filter(ip => ip.trim()))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="192.168.1.1&#10;10.0.0.1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  IP addresses that can access the platform during maintenance
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-medium text-blue-900 mb-2">General Settings Tips:</h5>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Site name appears in emails and throughout the platform</li>
          <li>• Site URL is used for generating links in emails</li>
          <li>• Support email receives system notifications and user inquiries</li>
          <li>• Maintenance mode blocks all users except allowed IPs</li>
          <li>• Feature flags control platform-wide functionality</li>
        </ul>
      </div>
    </div>
  );
};
